import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './PracticePage.css';
import NavigationButtons from '../components/NavigationButtons';
import PokoViewer from '../components/PokoViewer';
import poko from '../assets/poko_practice.png';
import correctSignDemo from '../assets/correct_sign_demo.jpg';

const API_URL = process.env.REACT_APP_API_URL;

// Generate an array of string numbers from '0' to '9'
const availableNumbers = Array.from({ length: 10 }, (_, i) => i.toString());

// Generate an array of uppercase letters from 'A' to 'Z'
const availableLetters = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));

// Import all sign images (letters, numbers, and common signs) from asset folders
function importAllSigns() {
	const letterCtx = require.context('../assets/signs/letter', false, /\.(png|jpe?g|svg)$/);
	const numberCtx = require.context('../assets/signs/number', false, /\.(png|jpe?g|svg)$/);

	// Helper function to extract file names and map them to their imported images
	const importSet = (ctx) => {
		const images = {};
		ctx.keys().forEach((key) => {
			const fileName = key.replace('./', '').replace(/\.(png|jpe?g|svg)$/, '');
			images[fileName] = ctx(key);
		});
		return images;
	};

	return {
		letters: importSet(letterCtx),
		numbers: importSet(numberCtx)
	};
}

function PracticePage() {

	console.log(
  "Request URL:",
  `${API_URL}/api/check-sign-language/`
);

	const navigate = useNavigate();
	const [firstVisit, setFirstVisit] = useState(true);
	const [showInstructions, setShowInstructions] = useState(false);
	const [showPermissionDeniedModal, setShowPermissionDeniedModal] = useState(false);

	// Track which signs the user has learned, grouped by category
	const [learntSigns, setLearntSigns] = useState({ letter: [], number: [] });

	// Load all sign images
	const { letters, numbers } = importAllSigns();

	const [showLearntSigns, SetShowLearntSigns] = useState(false);
	const filteredLetters = showLearntSigns
		? availableLetters.filter(l => learntSigns.letter.includes(l))
		: availableLetters;
	const filteredNumbers = showLearntSigns
		? availableNumbers.filter(n => learntSigns.number.includes(n))
		: availableNumbers;
	const [selectedSign, SetSelectedSign] = useState(letters.length > 0 ? letters[0] : 'A');
	const [signReferenceMode, SetSignReferenceMode] = useState('image');
	const [disableSignOptions, setDisableSignOptions] = useState(false);


	// Determine the animation name for a character
	const getAnimationForChar = (char, isFirstInSequence) => {
		if (!char) return 'Letter dummy';
		if (availableNumbers.includes(char)) return `Number ${char}`;
		if (availableLetters.includes(char.toUpperCase())) return `Letter ${char.toUpperCase()}`;
		return isFirstInSequence ? 'Number 0' : 'Letter dummy';
	};

	const [isModelLoading, setIsModelLoading] = useState(true); // Loading state for 3D model
	// Configuration for the PokoViewer 3D animation component
	const modelConfig = useMemo(() => ({
		scale: 2.2,
		animationSpeed: 0.6,
		currentAnimation: getAnimationForChar(selectedSign, false),
		isPlayingSequence: false,
		onModelLoaded: () => setIsModelLoading(false)
	}), [selectedSign]);


	const videoRef = useRef(null);
	const canvasRef = useRef(null);
	const mediaStreamRef = useRef(null);

	const [cameraActive, setCameraActive] = useState(false);
	const [countdown, setCountdown] = useState(null);
	const [snapshot, setSnapshot] = useState(null);

	const [matchResult, setMatchResult] = useState(null); // null = loading, true = match, false = mismatch

	// Load learnt signs from localStorage when component mounts
	useEffect(() => {
		const stored = localStorage.getItem('learntSigns');
		if (stored) {
			try {
				setLearntSigns(JSON.parse(stored));
			} catch (err) {
				console.error('Failed to parse learntSigns from localStorage:', err);
			}
		}
	}, []);

	const SignCard = () => {
		// Normalize selected sign
		const isLetter = letters[selectedSign] !== undefined;
		const src = isLetter ? letters[selectedSign] : numbers[selectedSign];

		if (!src) return null;

		return (
			<div className="sign-card">
				<h2 className='key'>{selectedSign}</h2>
				<img className='sign-img' src={src} alt={selectedSign} />
			</div>
		);
	};

	const PokoViewerWrapper = () => {
		return (
			<>
				<div className="model-container">
					{isModelLoading && (
						<div className="loading-indicator">
							<div className="loading-spinner"></div>
							<p>Loading 3D model...</p>
						</div>
					)}
					<PokoViewer config={modelConfig} key={selectedSign} />
				</div>
			</>
		);
	};

	const handleSignClick = (sign) => {
		setCameraActive(false);
		setSnapshot(null);
		SetSelectedSign(sign);
	}

	const handleSnapClick = () => {
		if (firstVisit) {
			setShowInstructions(true);
			return; // Don't activate camera yet
		}
		setMatchResult(null);
		setDisableSignOptions(true);
		setCameraActive(true);
	};


	// Modal that shows instructions for how to play the game (shown only on first visit or when triggered)
	const InstructionModal = () => {
		if (!showInstructions) return null;

		const justClose = () => {
			setShowInstructions(false);
		};

		return (
			<div className="instructions-modal-overlay practice-modal" onClick={justClose}>
				<div className="instructions-modal" onClick={(e) => e.stopPropagation()}>
					{!firstVisit && (
						<button className="close-button" onClick={justClose}>×</button>
					)}
					<h2>📷 How to Play?</h2>

					<div className="instruction-section">
						<h3>Camera Tips</h3>
						<img className='sign-demo' src={correctSignDemo} alt='correct camera usage demo' />
						<p>
							• 🧍 Only one person in the camera<br />
							• 🌟 Use a plain background<br />
							• ✋ Keep your hand still<br />
							• 📷 Stand a little away from the camera<br />
							• 👋 Show your whole hand <br />
							• 🧠 The camera tries its best, but sometimes it gets things wrong. Just try your best!
						</p>
					</div>

					<div className="instruction-section">
						<h3>Privacy</h3>
						<p>
							🔒 We don’t save your photo<br />
							🤖 The camera only checks your hand, then forgets it right after
						</p>
					</div>

					{firstVisit ? (
						<button className="game-btn" onClick={() => {
							setFirstVisit(false);
							setShowInstructions(false);
							setDisableSignOptions(true);
							setCameraActive(true);
						}}>
							Got it, let’s go!
						</button>
					) : (
						<button className="game-btn" onClick={justClose}>
							Close
						</button>
					)}
				</div>
			</div>
		);
	};

	const PermissionModal = () => {
		const justClose = () => setShowPermissionDeniedModal(false);

		if (!showPermissionDeniedModal) return null;

		return (
			<div className="instructions-modal-overlay" onClick={justClose}>
				<div className="instructions-modal" onClick={(e) => e.stopPropagation()}>
					<button className="close-button" onClick={justClose}>×</button>
					<h2>📷 Oops! Camera Access Denied</h2>
					<p>
						We couldn't turn on your camera. <br />
						Please allow camera access in your browser settings and try again!
					</p>
					<button className="game-btn" onClick={justClose}>
						Got it!
					</button>
				</div>
			</div>
		);
	};


	const stopCamera = () => {
		const stream = mediaStreamRef.current;
		if (stream) {
			stream.getTracks().forEach(track => {
				if (track.readyState === 'live') {
					track.stop();
				}
			});
			mediaStreamRef.current = null;
			if (videoRef.current) {
				videoRef.current.srcObject = null;
			}
			console.log("Camera stopped");
		} else {
			console.log('No stream to stop');
		}
	};


	useEffect(() => {
		if (cameraActive) {
			requestAnimationFrame(() => {
				if (videoRef.current) {
					navigator.mediaDevices.getUserMedia({ video: true })
						.then(stream => {
							mediaStreamRef.current = stream;
							if (videoRef.current) {
								videoRef.current.srcObject = stream;
							}
							startCountdown();
						})
						.catch(err => {
							console.error("Camera access error:", err);
							setCameraActive(false);
							setDisableSignOptions(false);
							setShowPermissionDeniedModal(true)
						});
				}
			});
		} else {
			stopCamera();
		}
	}, [cameraActive]);


	const startCountdown = () => {
		let seconds = 5;
		setCountdown(seconds);
		const interval = setInterval(() => {
			seconds -= 1;
			if (seconds === 0) {
				clearInterval(interval);
				setCountdown(null);
				takePhoto();
			} else {
				setCountdown(seconds);
			}
		}, 1000);
	};

	const takePhoto = async () => {
		const video = videoRef.current;
		const canvas = canvasRef.current;

		if (video && canvas) {
			canvas.width = video.videoWidth;
			canvas.height = video.videoHeight;
			const ctx = canvas.getContext('2d');
			ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
			const imageData = canvas.toDataURL('image/png');
			setSnapshot(imageData);
			setCameraActive(false);
			setDisableSignOptions(false);

			// Send to backend
			try {
				const response = await fetch(`${API_URL}/api/check-sign-language/`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						image: imageData,
						expected_gesture: selectedSign
					}),
				});

				if (!response.ok) {
					throw new Error('Failed to check sign');
				}

				const result = await response.json();
				console.log('API Response:', result);
				setMatchResult(result.is_correct);
			} catch (error) {
				console.error('Error checking sign:', error);
				setMatchResult(false);
			}
		}
	};

	useEffect(() => {
		return () => {
			stopCamera(); // <- global reusable function
		};
	}, []);

	return (
		<div className="practice-page page">
			<div className="white-wrapper container">
				<InstructionModal />
				<PermissionModal />
				<div className="page-title">
					<h1>📷 Practice</h1>
					<h5>Check if you are signning well</h5>
					<button className="help-button" onClick={() => setShowInstructions(true)}>
						❔
					</button>
				</div>
				<div className="speech-section">
					<div className="speech-bubble-left">
						<strong>
							Let’s practice signing!
						</strong>
						<p>
							Let’s practice signing with your hands! Choose a letter or number, copy the sign, and we’ll check how close your version is.
						</p>
						<div className='sign-options'>
							<div className="toggle-wrapper">
								<label className="switch">
									<input
										type="checkbox"
										checked={showLearntSigns}
										onChange={() => SetShowLearntSigns(!showLearntSigns)}
									/>
									<span className="slider round"></span>
								</label>
								<span className="toggle-label">Show only ✅ learned signs</span>
							</div>
							<div className='title'>📘 Letters:</div>
							<div className='sign-option'>
								{filteredLetters.length === 0 ? (
									<div className="no-signs-message">
										<p>You haven’t marked any letters as learned yet.</p>
										<button className="learn-link-btn" onClick={() => navigate('/learn')}>
											📘 Go to Learn Page
										</button>
									</div>
								) : (
									filteredLetters.map(letter => (
										<button key={letter}
											className={`sign-btn ${letter === selectedSign ? 'selected' : ''}`}
											onClick={() => handleSignClick(letter)}
											disabled={disableSignOptions}>
											{letter}
										</button>
									))
								)}
							</div>

							<div className='title'>🔢 Numbers:</div>
							<div className='sign-option'>

								{filteredNumbers.length === 0 ? (
									<div className="no-signs-message">
										<p>You haven’t marked any numbers as learned yet.</p>
										<button className="learn-link-btn" onClick={() => navigate('/learn')}>
											📘 Go to Learn Page
										</button>
									</div>
								) : (
									filteredNumbers.map(number => (
										<button key={number}
											className={`sign-btn ${number === selectedSign ? 'selected' : ''}`}
											onClick={() => handleSignClick(number)}
											disabled={disableSignOptions}>
											{number}
										</button>
									))
								)}
							</div>
						</div>

					</div>

					<img src={poko} alt="Buddy" className="poko" />
				</div>
				<div className='practice-wrapper'>
					<div className='reference-wrapper'>
						<strong>👀 Sign Reference</strong>
						<div className='mode-wrapper'>
							<button
								className={signReferenceMode === 'image' ? 'active' : ''}
								onClick={() => SetSignReferenceMode('image')}
							>
								Image
							</button>
							<button
								className={signReferenceMode === 'animation' ? 'active' : ''}
								onClick={() => SetSignReferenceMode('animation')}
							>
								Noko Animation
							</button>
						</div>

						<div className='slider-wrapper'>
							<div
								className='slider-track'
								style={{ transform: signReferenceMode === 'image' ? 'translateX(0%)' : 'translateX(-50%)' }}
							>
								<div className='slider-panel'>
									<SignCard />
									{['H', 'J'].includes(selectedSign) ? (
										<p>⚠️ Uh-oh! This sign likes to wiggle and move, so the camera can’t catch it just yet but NOKO can. <br />
											Try it out and show off your awesome moves! 🎉</p>
									) : (
										<>
											<p>📋 Match the pose shown in the image<br />
												We’ll take a photo to see how close your sign looks!</p>
											<button className="practice-btn" onClick={handleSnapClick}>
												{cameraActive ? 'Snapping...' : 'Snap My Sign!'}
											</button>
										</>
									)}

								</div>
								<div className='slider-panel'>
									<PokoViewerWrapper />
									{['H', 'J'].includes(selectedSign) ? (
										<p>🎞️ Watch how the sign is performed. </p>
									) : (
										<p>🎞️ Watch how the sign is performed. <br />
										When you're ready, switch to <strong>Image mode</strong> to copy the exact pose for snapping.</p>
									)}
								</div>
							</div>
						</div>



					</div>
					<div className='recognition-wrapper'>
						<strong>📷 Camera Recognition</strong>

						{!cameraActive && !snapshot && (
							<p className="camera-placeholder">Click "Snap My Sign!" to begin.</p>
						)}
						{cameraActive && (
							<>
								<video ref={videoRef} autoPlay playsInline className="video-preview" />
								{countdown !== null && <p className="recognition-msg countdown">Snapping in {countdown}...</p>}
							</>
						)}

						{!cameraActive && snapshot && (
							<>
								<div className='snap-effect'>
									<img src={snapshot} alt="Captured" className="snapshot-preview" />
								</div>
								<div className="match-result">
									{matchResult === null && (
										<div className="result-loading">🔍 Checking your sign...</div>
									)}

									{matchResult === true && (
										<div className="result-success recognition-msg">
											👍 You got it! That’s a perfect match!
										</div>
									)}

									{matchResult === false && (
										<div className="result-fail recognition-msg">
											🤔 Almost there! Look at the fingers and try again!
										</div>
									)}

									{(matchResult !== null) && (
										<div className="result-buttons">
											<button className="practice-btn" 
												onClick={() => {
													setMatchResult(null);
													setCameraActive(true);
													setDisableSignOptions(true);
												}}>Try Again</button>
											<button className="practice-btn" onClick={() => {
												setSnapshot(null);
												setMatchResult(null);
												window.scrollTo({ top: 0, behavior: 'smooth' });
											}}>
												Practice Another Sign
											</button>
										</div>
									)}
								</div>

							</>
						)}

						{/* Hidden canvas used to capture the photo */}
						<canvas ref={canvasRef} style={{ display: 'none' }} />
					</div>

				</div>
			</div>

			<NavigationButtons />
		</div >
	)
}

export default PracticePage;